import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import cigaretteBaselineImage from "./assets/cigarette-lit-baseline.png";
import cigaretteStrongImage from "./assets/cigarette-lit-strong.png";
import savedBreathModel from "./smoking/breathModel.json";
import savedBreathSamples from "./smoking/breathSamples.json";
import "./smoking.css";

const MODEL_STORAGE_KEY = "henry-smoking-breath-model-v1";
const SAMPLES_STORAGE_KEY = "henry-smoking-breath-samples-v1";
const TRAINING_DURATION = 2400;
const featureWeights = savedBreathModel.featureWeights;
const defaultModel = savedBreathModel.model;
const defaultSamples = savedBreathSamples;

function clamp(value, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function isUsableModel(model) {
  return Boolean(
    model?.inhale?.centroid?.length === featureWeights.length &&
    model?.exhale?.centroid?.length === featureWeights.length
  );
}

function normalizeModel(model) {
  if (!isUsableModel(model)) {
    return defaultModel;
  }

  return {
    inhale: {
      count: Number(model.inhale.count) || 0,
      centroid: model.inhale.centroid.map(Number),
    },
    exhale: {
      count: Number(model.exhale.count) || 0,
      centroid: model.exhale.centroid.map(Number),
    },
  };
}

function loadStoredModel() {
  if (typeof window === "undefined") {
    return defaultModel;
  }

  try {
    const stored = window.localStorage.getItem(MODEL_STORAGE_KEY);
    if (!stored) {
      return defaultModel;
    }

    return normalizeModel(JSON.parse(stored));
  } catch {
    return defaultModel;
  }
}

function normalizeFrame(frame) {
  if (!Array.isArray(frame) || frame.length !== featureWeights.length) {
    return null;
  }

  return frame.map((value) => Number(Number(value).toFixed(6)));
}

function normalizeSampleSession(session) {
  const label = session?.label;
  if (label !== "inhale" && label !== "exhale") {
    return null;
  }

  const frames = (session.frames || []).map(normalizeFrame).filter(Boolean);
  if (!frames.length) {
    return null;
  }

  const centroid = normalizeFrame(session.centroid || averageFeatures(frames));

  return {
    id: String(session.id || `${label}-${Date.now()}`),
    label,
    createdAt: String(session.createdAt || new Date().toISOString()),
    durationMs: Math.round(Number(session.durationMs) || TRAINING_DURATION),
    frameCount: frames.length,
    sensitivity: Number(Number(session.sensitivity || savedBreathModel.sensitivity || 1.7).toFixed(2)),
    centroid,
    frames,
  };
}

function normalizeSamples(samples) {
  return {
    version: Number(samples?.version) || defaultSamples.version,
    source: String(samples?.source || defaultSamples.source),
    featureNames: defaultSamples.featureNames,
    sessions: (samples?.sessions || []).map(normalizeSampleSession).filter(Boolean),
  };
}

function loadStoredSamples() {
  if (typeof window === "undefined") {
    return defaultSamples;
  }

  try {
    const stored = window.localStorage.getItem(SAMPLES_STORAGE_KEY);
    if (!stored) {
      return normalizeSamples(defaultSamples);
    }

    return normalizeSamples(JSON.parse(stored));
  } catch {
    return normalizeSamples(defaultSamples);
  }
}

function makeSampleSession(session, centroid, sensitivity) {
  return normalizeSampleSession({
    id: `${session.label}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    label: session.label,
    createdAt: new Date().toISOString(),
    durationMs: performance.now() - session.startedAt,
    frameCount: session.frames.length,
    sensitivity,
    centroid,
    frames: session.frames,
  });
}

function distance(a, b) {
  return Math.sqrt(
    a.reduce((sum, value, index) => {
      const delta = (value - b[index]) * featureWeights[index];
      return sum + delta * delta;
    }, 0)
  );
}

function averageFeatures(frames) {
  const totals = new Array(frames[0].length).fill(0);
  frames.forEach((frame) => {
    frame.forEach((value, index) => {
      totals[index] += value;
    });
  });

  return totals.map((value) => value / frames.length);
}

function mergeModel(model, label, centroid, frameCount) {
  const previous = model[label];
  const previousWeight = Math.min(previous.count, 90);
  const nextWeight = Math.min(frameCount, 90);
  const totalWeight = previousWeight + nextWeight || 1;

  return {
    ...model,
    [label]: {
      count: previous.count + frameCount,
      centroid: centroid.map((value, index) => {
        const previousValue = previous.centroid[index] ?? value;
        return (previousValue * previousWeight + value * nextWeight) / totalWeight;
      }),
    },
  };
}

function classifyBreath(features, model, activity) {
  const inhaleDistance = distance(features, model.inhale.centroid);
  const exhaleDistance = distance(features, model.exhale.centroid);
  const bestLabel = inhaleDistance <= exhaleDistance ? "inhale" : "exhale";
  const bestDistance = Math.min(inhaleDistance, exhaleDistance);
  const otherDistance = Math.max(inhaleDistance, exhaleDistance);
  const trainedEnough = model.inhale.count >= 18 && model.exhale.count >= 18;

  if (trainedEnough) {
    const separation = clamp((otherDistance - bestDistance) / (otherDistance + 0.001), 0, 1);
    return {
      label: bestLabel,
      confidence: clamp(separation * 1.5 + activity * 0.35),
    };
  }

  const [, , centroid, highRatio, lowRatio, zeroCrossings, flux] = features;
  const inhaleScore = centroid * 1.2 + highRatio * 1.15 + zeroCrossings * 0.45 + flux * 0.25;
  const exhaleScore = lowRatio * 1.4 + (1 - centroid) * 0.55;
  const label = inhaleScore >= exhaleScore ? "inhale" : "exhale";
  const gap = Math.abs(inhaleScore - exhaleScore);

  return {
    label,
    confidence: clamp(gap * 1.3 + activity * 0.3),
  };
}

function extractFeatures(timeData, frequencyData, sampleRate, noiseFloor, previousFrequencyRef) {
  let sumSquares = 0;
  let zeroCrossings = 0;
  let previousSign = 0;

  for (let index = 0; index < timeData.length; index += 1) {
    const sample = (timeData[index] - 128) / 128;
    sumSquares += sample * sample;

    const sign = sample >= 0 ? 1 : -1;
    if (index > 0 && sign !== previousSign) {
      zeroCrossings += 1;
    }
    previousSign = sign;
  }

  const rms = Math.sqrt(sumSquares / timeData.length);
  const binHz = sampleRate / (frequencyData.length * 2);
  let totalEnergy = 0;
  let lowEnergy = 0;
  let highEnergy = 0;
  let centroidSum = 0;
  let flux = 0;
  const previousFrequency = previousFrequencyRef.current;

  for (let index = 1; index < frequencyData.length; index += 1) {
    const magnitude = frequencyData[index] / 255;
    const energy = magnitude * magnitude;
    const hz = index * binHz;
    totalEnergy += energy;
    centroidSum += energy * hz;

    if (hz < 500) {
      lowEnergy += energy;
    } else if (hz >= 2200 && hz < 7600) {
      highEnergy += energy;
    }

    if (previousFrequency) {
      const delta = magnitude - previousFrequency[index];
      if (delta > 0) {
        flux += delta;
      }
    }
  }

  previousFrequencyRef.current = Float32Array.from(frequencyData, (value) => value / 255);

  const safeEnergy = totalEnergy || 1;
  const nyquist = sampleRate / 2;
  const centroid = clamp((totalEnergy ? centroidSum / totalEnergy : 0) / nyquist);
  const normalizedRms = clamp((rms - noiseFloor) * 7);
  const energyShape = clamp(Math.log10(1 + totalEnergy) / 2.4);
  const lowRatio = clamp(lowEnergy / safeEnergy);
  const highRatio = clamp(highEnergy / safeEnergy);
  const zcr = clamp((zeroCrossings / timeData.length) * 5);
  const normalizedFlux = clamp(flux / frequencyData.length);

  return {
    rms,
    features: [
      normalizedRms,
      energyShape,
      centroid,
      highRatio,
      lowRatio,
      zcr,
      normalizedFlux,
    ],
  };
}

function modelExport(model, sensitivity) {
  return {
    version: savedBreathModel.version,
    source: "browser-calibration",
    featureNames: savedBreathModel.featureNames,
    featureWeights,
    sensitivity,
    model: normalizeModel(model),
  };
}

function useBreathDetector() {
  const [audioStatus, setAudioStatus] = useState("idle");
  const [mode, setMode] = useState("quiet");
  const [levels, setLevels] = useState({
    inhale: 0,
    exhale: 0,
    loudness: 0,
    confidence: 0,
  });
  const [training, setTraining] = useState(null);
  const [model, setModel] = useState(loadStoredModel);
  const [samples, setSamples] = useState(loadStoredSamples);
  const [message, setMessage] = useState("Microphone is off");
  const [sensitivity, setSensitivity] = useState(savedBreathModel.sensitivity || 1.7);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const animationRef = useRef(0);
  const timeDataRef = useRef(null);
  const frequencyDataRef = useRef(null);
  const previousFrequencyRef = useRef(null);
  const noiseFloorRef = useRef(0.018);
  const levelsRef = useRef(levels);
  const modelRef = useRef(model);
  const sensitivityRef = useRef(sensitivity);
  const trainingRef = useRef(null);
  const smoothedRef = useRef({ inhale: 0, exhale: 0, loudness: 0, confidence: 0 });

  const trainedFrames = model.inhale.count + model.exhale.count;
  const isListening = audioStatus === "listening";
  const isStarting = audioStatus === "starting";

  useEffect(() => {
    levelsRef.current = levels;
  }, [levels]);

  useEffect(() => {
    modelRef.current = model;
    window.localStorage.setItem(MODEL_STORAGE_KEY, JSON.stringify(model));
  }, [model]);

  useEffect(() => {
    window.localStorage.setItem(SAMPLES_STORAGE_KEY, JSON.stringify(samples));
  }, [samples]);

  useEffect(() => {
    sensitivityRef.current = sensitivity;
  }, [sensitivity]);

  const stopMic = useCallback(() => {
    cancelAnimationFrame(animationRef.current);

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    timeDataRef.current = null;
    frequencyDataRef.current = null;
    previousFrequencyRef.current = null;
    trainingRef.current = null;
    setTraining(null);
    setAudioStatus("idle");
    setMode("quiet");
    setLevels({ inhale: 0, exhale: 0, loudness: 0, confidence: 0 });
    setMessage("Microphone is off");
  }, []);

  useEffect(() => {
    return () => stopMic();
  }, [stopMic]);

  const finishTraining = useCallback((session) => {
    const frames = session.frames;

    if (frames.length < 10) {
      setMessage(`Try ${session.label} again a little closer to the mic`);
      setTraining(null);
      trainingRef.current = null;
      return;
    }

    const centroid = averageFeatures(frames);
    const sampleSession = makeSampleSession(session, centroid, sensitivityRef.current);
    if (sampleSession) {
      setSamples((currentSamples) => ({
        ...currentSamples,
        source: "browser-recorded-feature-sessions",
        sessions: [...currentSamples.sessions, sampleSession],
      }));
    }
    setModel((currentModel) => mergeModel(currentModel, session.label, centroid, frames.length));
    setMessage(`${session.label === "inhale" ? "Inhale" : "Exhale"} sample saved`);
    setTraining(null);
    trainingRef.current = null;
  }, []);

  const analyze = useCallback(() => {
    const analyser = analyserRef.current;
    const audioContext = audioContextRef.current;

    if (!analyser || !audioContext || !timeDataRef.current || !frequencyDataRef.current) {
      return;
    }

    analyser.getByteTimeDomainData(timeDataRef.current);
    analyser.getByteFrequencyData(frequencyDataRef.current);

    const { rms, features } = extractFeatures(
      timeDataRef.current,
      frequencyDataRef.current,
      audioContext.sampleRate,
      noiseFloorRef.current,
      previousFrequencyRef
    );

    if (rms < noiseFloorRef.current * 1.7) {
      noiseFloorRef.current = noiseFloorRef.current * 0.985 + rms * 0.015;
    }

    const sensitivityLevel = sensitivityRef.current;
    const threshold = Math.max(0.0065, noiseFloorRef.current * (2.15 - sensitivityLevel * 0.32));
    const activity = clamp((rms - threshold) / (threshold * (5.6 / sensitivityLevel)));
    const activeEnough = activity > 0.022;
    const classification = activeEnough
      ? classifyBreath(features, modelRef.current, activity)
      : { label: "quiet", confidence: 0 };

    let targetInhale = 0;
    let targetExhale = 0;

    if (classification.label === "inhale") {
      targetInhale = activity * clamp(classification.confidence + 0.28, 0, 1);
    } else if (classification.label === "exhale") {
      targetExhale = activity * clamp(classification.confidence + 0.28, 0, 1);
    }

    const smoothing = 0.24;
    smoothedRef.current = {
      inhale: smoothedRef.current.inhale * (1 - smoothing) + targetInhale * smoothing,
      exhale: smoothedRef.current.exhale * (1 - smoothing) + targetExhale * smoothing,
      loudness: smoothedRef.current.loudness * 0.82 + activity * 0.18,
      confidence: smoothedRef.current.confidence * 0.82 + classification.confidence * 0.18,
    };

    const session = trainingRef.current;
    if (session) {
      if (activeEnough) {
        session.frames.push(features);
      }

      const elapsed = performance.now() - session.startedAt;
      const progress = clamp(elapsed / TRAINING_DURATION);
      setTraining({ label: session.label, progress });

      if (elapsed >= TRAINING_DURATION) {
        finishTraining(session);
      }
    }

    const nextLevels = {
      inhale: clamp(smoothedRef.current.inhale),
      exhale: clamp(smoothedRef.current.exhale),
      loudness: clamp(smoothedRef.current.loudness),
      confidence: clamp(smoothedRef.current.confidence),
    };

    levelsRef.current = nextLevels;
    setLevels(nextLevels);

    if (nextLevels.inhale > 0.045) {
      setMode("inhale");
    } else if (nextLevels.exhale > 0.045) {
      setMode("exhale");
    } else {
      setMode("quiet");
    }

    animationRef.current = requestAnimationFrame(analyze);
  }, [finishTraining]);

  const startMic = useCallback(async () => {
    try {
      setAudioStatus("starting");
      setMessage("Requesting microphone");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: true,
        },
      });

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.72;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      mediaStreamRef.current = stream;
      timeDataRef.current = new Uint8Array(analyser.fftSize);
      frequencyDataRef.current = new Uint8Array(analyser.frequencyBinCount);
      previousFrequencyRef.current = null;
      noiseFloorRef.current = 0.018;
      smoothedRef.current = { inhale: 0, exhale: 0, loudness: 0, confidence: 0 };
      setAudioStatus("listening");
      setMessage("Listening");
      animationRef.current = requestAnimationFrame(analyze);
    } catch (error) {
      setAudioStatus("blocked");
      setMessage(error?.name === "NotAllowedError" ? "Microphone permission was blocked" : "Could not start microphone");
    }
  }, [analyze]);

  const startTraining = useCallback((label) => {
    if (!isListening) {
      setMessage("Start the mic first");
      return;
    }

    const session = {
      label,
      startedAt: performance.now(),
      frames: [],
    };
    trainingRef.current = session;
    setTraining({ label, progress: 0 });
    setMessage(label === "inhale" ? "Recording inhale" : "Recording exhale");
  }, [isListening]);

  const resetModel = useCallback(() => {
    setModel(defaultModel);
    window.localStorage.removeItem(MODEL_STORAGE_KEY);
    setMessage("Model reset");
  }, []);

  const resetSamples = useCallback(() => {
    setSamples(normalizeSamples(defaultSamples));
    window.localStorage.removeItem(SAMPLES_STORAGE_KEY);
    setMessage("Sample dataset reset");
  }, []);

  const exportJson = useMemo(() => (
    JSON.stringify(modelExport(model, sensitivity), null, 2)
  ), [model, sensitivity]);

  const samplesJson = useMemo(() => (
    JSON.stringify(normalizeSamples(samples), null, 2)
  ), [samples]);

  const copyExport = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(exportJson);
      setMessage("Model JSON copied");
    } catch {
      setMessage("Could not copy model JSON");
    }
  }, [exportJson]);

  const copySamples = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(samplesJson);
      setMessage("Sample dataset JSON copied");
    } catch {
      setMessage("Could not copy sample dataset");
    }
  }, [samplesJson]);

  const sampleFrameCount = samples.sessions.reduce((total, session) => total + session.frameCount, 0);

  return {
    audioStatus,
    copyExport,
    copySamples,
    exportJson,
    isListening,
    isStarting,
    levels,
    levelsRef,
    message,
    mode,
    model,
    resetSamples,
    sampleFrameCount,
    sampleSessionCount: samples.sessions.length,
    samplesJson,
    resetModel,
    sensitivity,
    setSensitivity,
    startMic,
    startTraining,
    stopMic,
    trainedFrames,
    training,
  };
}

function getCigaretteMetrics(width, height) {
  const imageWidth = Math.min(width * 0.86, 1120);
  const imageHeight = imageWidth * (321 / 1431);
  const imageLeft = width / 2 - imageWidth / 2;
  const imageTop = height * 0.48 - imageHeight / 2;

  return {
    imageHeight,
    imageLeft,
    imageTop,
    imageWidth,
    tipX: imageLeft + imageWidth * 0.95,
    tipY: imageTop + imageHeight * 0.23,
  };
}

function RealisticSmokeCanvas({ levelsRef }) {
  const canvasRef = useRef(null);
  const cigaretteImagesRef = useRef({
    baseline: null,
    strong: null,
    baselineLoaded: false,
    strongLoaded: false,
  });
  const particlesRef = useRef([]);
  const emissionRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    let animationFrame = 0;
    let previousTime = performance.now();
    const baselineCigarette = new Image();
    const strongCigarette = new Image();
    baselineCigarette.onload = () => {
      cigaretteImagesRef.current = {
        ...cigaretteImagesRef.current,
        baseline: baselineCigarette,
        baselineLoaded: true,
      };
    };
    strongCigarette.onload = () => {
      cigaretteImagesRef.current = {
        ...cigaretteImagesRef.current,
        strong: strongCigarette,
        strongLoaded: true,
      };
    };
    baselineCigarette.src = cigaretteBaselineImage;
    strongCigarette.src = cigaretteStrongImage;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const pixelRatio = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.floor(rect.width * pixelRatio));
      canvas.height = Math.max(1, Math.floor(rect.height * pixelRatio));
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    };

    const addSmokeParticle = (originX, originY, power) => {
      particlesRef.current.push({
        x: originX + Math.random() * 18 - 9,
        y: originY + Math.random() * 12 - 6,
        vx: -0.13 - Math.random() * 0.36 - power * 0.34,
        vy: -0.15 - Math.random() * 0.36 - power * 0.4,
        radius: 8 + Math.random() * 16 + power * 22,
        life: 0,
        maxLife: 2600 + Math.random() * 2600 + power * 1200,
        alpha: 0.08 + Math.random() * 0.16 + power * 0.14,
        curl: Math.random() * Math.PI * 2,
        curlSpeed: 0.002 + Math.random() * 0.005,
        stretch: 1.4 + Math.random() * 2.2,
      });
    };

    const draw = (time) => {
      const rect = canvas.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      const delta = Math.min(34, time - previousTime || 16);
      previousTime = time;
      context.clearRect(0, 0, width, height);

      const cigaretteMetrics = getCigaretteMetrics(width, height);
      const { tipX, tipY } = cigaretteMetrics;
      const levels = levelsRef.current;
      const inhaleLevel = levels.inhale;
      const exhaleLevel = levels.exhale;
      const idleLevel = levels.loudness < 0.02 ? 0.04 : 0;
      const smokePower = Math.max(exhaleLevel, idleLevel);

      emissionRef.current += smokePower * delta * (0.018 + exhaleLevel * 0.06);
      while (emissionRef.current > 1) {
        addSmokeParticle(tipX, tipY, smokePower);
        emissionRef.current -= 1;
      }

      const cigaretteImages = cigaretteImagesRef.current;
      if (cigaretteImages.baselineLoaded) {
        context.save();
        context.shadowColor = "rgba(0, 0, 0, 0.62)";
        context.shadowBlur = 44;
        context.shadowOffsetY = 34;
        context.drawImage(
          cigaretteImages.baseline,
          cigaretteMetrics.imageLeft,
          cigaretteMetrics.imageTop,
          cigaretteMetrics.imageWidth,
          cigaretteMetrics.imageHeight
        );
        context.restore();

        if (cigaretteImages.strongLoaded) {
          const inhaleHeat = clamp(inhaleLevel * 1.55);
          if (inhaleHeat > 0.015) {
            context.save();
            context.globalAlpha = inhaleHeat;
            context.drawImage(
              cigaretteImages.strong,
              cigaretteMetrics.imageLeft,
              cigaretteMetrics.imageTop,
              cigaretteMetrics.imageWidth,
              cigaretteMetrics.imageHeight
            );
            context.restore();
          }
        }
      }

      particlesRef.current = particlesRef.current.filter((particle) => {
        particle.life += delta;
        const age = clamp(particle.life / particle.maxLife);
        particle.curl += delta * particle.curlSpeed;
        particle.vx += Math.sin(particle.curl) * 0.0028 * delta;
        particle.vy -= 0.0007 * delta;
        particle.x += particle.vx * delta * 0.08;
        particle.y += particle.vy * delta * 0.08;
        particle.radius += delta * (0.011 + exhaleLevel * 0.014);

        const alpha = particle.alpha * Math.sin(Math.PI * age);
        context.save();
        context.translate(particle.x, particle.y);
        context.rotate(Math.sin(particle.curl) * 0.36);
        context.scale(particle.stretch, 1);
        const plume = context.createRadialGradient(0, 0, 2, 0, 0, particle.radius);
        plume.addColorStop(0, `rgba(232, 236, 220, ${alpha})`);
        plume.addColorStop(0.5, `rgba(184, 193, 181, ${alpha * 0.48})`);
        plume.addColorStop(1, "rgba(184, 193, 181, 0)");
        context.fillStyle = plume;
        context.beginPath();
        context.arc(0, 0, particle.radius, 0, Math.PI * 2);
        context.fill();
        context.restore();

        return particle.life < particle.maxLife && particle.y > -particle.radius * 2;
      });

      animationFrame = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener("resize", resize);
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    resizeObserver?.observe(canvas);
    animationFrame = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", resize);
      resizeObserver?.disconnect();
      cancelAnimationFrame(animationFrame);
    };
  }, [levelsRef]);

  return <canvas ref={canvasRef} className="absolute inset-0 z-20 h-full w-full" aria-hidden="true" />;
}

function DebugSmokingCanvas({ levelsRef }) {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const emberRef = useRef([]);
  const emissionRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    let animationFrame = 0;
    let previousTime = performance.now();

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const pixelRatio = window.devicePixelRatio || 1;
      canvas.width = Math.floor(rect.width * pixelRatio);
      canvas.height = Math.floor(rect.height * pixelRatio);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    };

    const addSmokeParticle = (originX, originY, power, width, height) => {
      const sideDraft = (Math.random() - 0.5) * (0.7 + power * 1.4);
      particlesRef.current.push({
        x: originX + (Math.random() - 0.5) * 18,
        y: originY + (Math.random() - 0.5) * 12,
        vx: sideDraft,
        vy: -0.42 - Math.random() * (0.55 + power * 0.95),
        radius: 8 + Math.random() * 18 + power * 14,
        life: 0,
        maxLife: 2200 + Math.random() * 2100 + power * 900,
        wobble: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.035,
        alpha: 0.1 + Math.random() * 0.14 + power * 0.12,
        stretch: 1.1 + Math.random() * 1.8,
        boundary: { width, height },
      });
    };

    const addEmber = (originX, originY, power) => {
      emberRef.current.push({
        x: originX + Math.random() * 10 - 5,
        y: originY + Math.random() * 8 - 4,
        vx: -0.3 - Math.random() * 0.8,
        vy: -0.25 - Math.random() * 0.5,
        life: 0,
        maxLife: 450 + Math.random() * 450,
        size: 1.2 + Math.random() * 3.2 + power * 2,
      });
    };

    const drawRoundedRect = (x, y, width, height, radius) => {
      context.beginPath();
      context.moveTo(x + radius, y);
      context.lineTo(x + width - radius, y);
      context.quadraticCurveTo(x + width, y, x + width, y + radius);
      context.lineTo(x + width, y + height - radius);
      context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      context.lineTo(x + radius, y + height);
      context.quadraticCurveTo(x, y + height, x, y + height - radius);
      context.lineTo(x, y + radius);
      context.quadraticCurveTo(x, y, x + radius, y);
      context.closePath();
    };

    const drawCigarette = (width, height, inhaleLevel, exhaleLevel, time) => {
      const cigaretteLength = Math.min(width * 0.58, 560);
      const cigaretteHeight = Math.max(22, Math.min(width * 0.035, 34));
      const x = width * 0.5 - cigaretteLength * 0.5;
      const y = height * 0.54;
      const tipX = x + cigaretteLength;
      const tipY = y + cigaretteHeight * 0.5;
      const tilt = -0.055;

      context.save();
      context.translate(width * 0.5, tipY);
      context.rotate(tilt);
      context.translate(-width * 0.5, -tipY);

      context.shadowColor = "rgba(0, 0, 0, 0.34)";
      context.shadowBlur = 22;
      context.shadowOffsetY = 18;
      drawRoundedRect(x, y, cigaretteLength, cigaretteHeight, cigaretteHeight / 2);
      const paperGradient = context.createLinearGradient(x, y, x, y + cigaretteHeight);
      paperGradient.addColorStop(0, "#fff7df");
      paperGradient.addColorStop(0.45, "#e7dcc2");
      paperGradient.addColorStop(1, "#bcb39f");
      context.fillStyle = paperGradient;
      context.fill();

      context.shadowColor = "transparent";
      drawRoundedRect(x, y, cigaretteLength * 0.22, cigaretteHeight, cigaretteHeight / 2);
      const filterGradient = context.createLinearGradient(x, y, x + cigaretteLength * 0.22, y);
      filterGradient.addColorStop(0, "#b47a32");
      filterGradient.addColorStop(0.58, "#d6a45a");
      filterGradient.addColorStop(1, "#7a4a20");
      context.fillStyle = filterGradient;
      context.fill();

      context.globalAlpha = 0.28;
      for (let index = 0; index < 6; index += 1) {
        context.beginPath();
        context.moveTo(x + cigaretteLength * 0.24 + index * 40, y + 4);
        context.lineTo(x + cigaretteLength * 0.19 + index * 40, y + cigaretteHeight - 4);
        context.strokeStyle = "#7c715e";
        context.lineWidth = 1;
        context.stroke();
      }
      context.globalAlpha = 1;

      const ashWidth = 32;
      drawRoundedRect(tipX - ashWidth, y + 2, ashWidth + 4, cigaretteHeight - 4, cigaretteHeight / 2);
      const ashGradient = context.createLinearGradient(tipX - ashWidth, y, tipX + 10, y);
      ashGradient.addColorStop(0, "#d8d4c9");
      ashGradient.addColorStop(0.7, "#5d5a54");
      ashGradient.addColorStop(1, "#1b1815");
      context.fillStyle = ashGradient;
      context.fill();

      const pulse = 0.55 + Math.sin(time * 0.009) * 0.12;
      const heat = clamp(inhaleLevel * 0.95 + exhaleLevel * 0.2 + pulse * 0.12);
      const glowRadius = 24 + heat * 88;
      const glow = context.createRadialGradient(tipX + 5, tipY, 3, tipX + 5, tipY, glowRadius);
      glow.addColorStop(0, `rgba(255, 244, 173, ${0.78 + heat * 0.2})`);
      glow.addColorStop(0.18, `rgba(255, 96, 32, ${0.52 + heat * 0.28})`);
      glow.addColorStop(0.52, `rgba(222, 45, 24, ${0.18 + heat * 0.22})`);
      glow.addColorStop(1, "rgba(222, 45, 24, 0)");
      context.fillStyle = glow;
      context.beginPath();
      context.arc(tipX + 5, tipY, glowRadius, 0, Math.PI * 2);
      context.fill();

      const emberGradient = context.createRadialGradient(tipX + 2, tipY, 1, tipX + 2, tipY, 15 + heat * 11);
      emberGradient.addColorStop(0, "#fff6b7");
      emberGradient.addColorStop(0.38, "#ff642e");
      emberGradient.addColorStop(1, "#4a1711");
      context.fillStyle = emberGradient;
      context.beginPath();
      context.ellipse(tipX + 2, tipY, 10 + heat * 4, cigaretteHeight * 0.43, 0, 0, Math.PI * 2);
      context.fill();

      context.restore();

      return { x: tipX + 2, y: tipY - 18 };
    };

    const draw = (time) => {
      const rect = canvas.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      const delta = Math.min(34, time - previousTime || 16);
      previousTime = time;

      context.clearRect(0, 0, width, height);

      const background = context.createLinearGradient(0, 0, 0, height);
      background.addColorStop(0, "#191713");
      background.addColorStop(0.58, "#282219");
      background.addColorStop(1, "#0f1010");
      context.fillStyle = background;
      context.fillRect(0, 0, width, height);

      const levels = levelsRef.current;
      const inhaleLevel = levels.inhale;
      const exhaleLevel = levels.exhale;
      const origin = drawCigarette(width, height, inhaleLevel, exhaleLevel, time);

      if (inhaleLevel > 0.45 && Math.random() < inhaleLevel * 0.45) {
        addEmber(origin.x, origin.y + 18, inhaleLevel);
      }

      emissionRef.current += exhaleLevel * delta * (0.025 + exhaleLevel * 0.032);
      while (emissionRef.current > 1) {
        addSmokeParticle(origin.x + 12, origin.y, exhaleLevel, width, height);
        emissionRef.current -= 1;
      }

      particlesRef.current = particlesRef.current.filter((particle) => {
        particle.life += delta;
        const age = particle.life / particle.maxLife;
        particle.wobble += particle.spin * delta;
        particle.vx += Math.sin(particle.wobble) * 0.006 * delta;
        particle.vy -= 0.0007 * delta;
        particle.x += particle.vx * delta * 0.052;
        particle.y += particle.vy * delta * 0.052;
        particle.radius += delta * (0.012 + exhaleLevel * 0.01);

        const alpha = particle.alpha * Math.sin(Math.PI * clamp(age));
        context.save();
        context.translate(particle.x, particle.y);
        context.rotate(Math.sin(particle.wobble) * 0.4);
        context.scale(particle.stretch, 1);
        const gradient = context.createRadialGradient(0, 0, 2, 0, 0, particle.radius);
        gradient.addColorStop(0, `rgba(237, 241, 229, ${alpha})`);
        gradient.addColorStop(0.52, `rgba(179, 190, 177, ${alpha * 0.52})`);
        gradient.addColorStop(1, "rgba(179, 190, 177, 0)");
        context.fillStyle = gradient;
        context.beginPath();
        context.arc(0, 0, particle.radius, 0, Math.PI * 2);
        context.fill();
        context.restore();

        return particle.life < particle.maxLife && particle.y > -particle.radius * 2;
      });

      emberRef.current = emberRef.current.filter((ember) => {
        ember.life += delta;
        ember.x += ember.vx * delta * 0.06;
        ember.y += ember.vy * delta * 0.06;
        ember.vy += 0.0008 * delta;
        const age = ember.life / ember.maxLife;
        const alpha = clamp(1 - age);

        context.fillStyle = `rgba(255, ${Math.round(120 + alpha * 100)}, 39, ${alpha})`;
        context.beginPath();
        context.arc(ember.x, ember.y, ember.size * alpha, 0, Math.PI * 2);
        context.fill();

        return ember.life < ember.maxLife;
      });

      animationFrame = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener("resize", resize);
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    resizeObserver?.observe(canvas);
    animationFrame = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", resize);
      resizeObserver?.disconnect();
      cancelAnimationFrame(animationFrame);
    };
  }, [levelsRef]);

  return (
    <canvas
      ref={canvasRef}
      className="h-[48vh] min-h-[280px] w-full overflow-hidden rounded-none sm:h-[56vh] sm:min-h-[320px]"
      aria-label="Animated cigarette responding to microphone input"
    />
  );
}

function Meter({ label, value, colorClass }) {
  return (
    <div className="min-w-0">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-xs font-semibold uppercase text-[#d8d1c4]">{label}</span>
        <span className="font-mono text-xs text-[#aaa196]">{Math.round(value * 100)}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-[#312d28]">
        <div
          className={`h-full rounded-full ${colorClass}`}
          style={{ width: `${Math.round(value * 100)}%` }}
        />
      </div>
    </div>
  );
}

function SensitivityControl({ value, onChange }) {
  return (
    <label className="block">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-xs font-semibold uppercase text-[#d8d1c4]">sensitivity</span>
        <span className="font-mono text-xs text-[#aaa196]">{value.toFixed(1)}x</span>
      </div>
      <input
        type="range"
        min="0.7"
        max="2.8"
        step="0.1"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="h-11 w-full accent-[#f5cf58]"
      />
    </label>
  );
}

function BreathButton({ detector, compact = false }) {
  const label = detector.isStarting ? "Starting..." : detector.isListening ? "Stop mic" : "Start mic";

  return (
    <button
      type="button"
      onClick={detector.isListening ? detector.stopMic : detector.startMic}
      disabled={detector.isStarting}
      className={`rounded-sm bg-[#f5efe4] font-bold text-[#11100f] transition hover:bg-[#fffaf1] disabled:cursor-wait disabled:bg-[#8a8176] ${
        compact ? "px-4 py-3 text-sm" : "px-6 py-4 text-base"
      }`}
    >
      {label}
    </button>
  );
}

function PublicSmokingScene({ detector }) {
  const metricsStyle = {
    "--inhale": detector.levels.inhale,
    "--exhale": detector.levels.exhale,
  };

  const retryMic = () => {
    if (!detector.isListening && !detector.isStarting) {
      detector.startMic();
    }
  };

  return (
    <main
      className="smokingPage relative overflow-hidden bg-[#0b0a09] text-[#f4efe5]"
      style={metricsStyle}
      onClick={retryMic}
      onTouchStart={retryMic}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,rgba(99,75,48,0.26),rgba(11,10,9,0)_42%),linear-gradient(180deg,#17130f_0%,#0b0a09_68%,#060606_100%)]" />

      <section className="smokingScene relative z-10 overflow-hidden" aria-label="Interactive smoke scene">
        <RealisticSmokeCanvas levelsRef={detector.levelsRef} />
      </section>
    </main>
  );
}

function modeLabel(detector) {
  if (detector.training) {
    return detector.training.label === "inhale" ? "training inhale" : "training exhale";
  }

  if (!detector.isListening) {
    return "standby";
  }

  return detector.mode;
}

function SmokingDebugPage() {
  const detector = useBreathDetector();

  return (
    <main className="smokingDebugPage min-h-screen bg-[#11100f] text-[#f4efe5]">
      <section className="mx-auto flex min-h-screen w-full max-w-7xl flex-col">
        <div className="flex flex-col gap-4 px-5 pb-4 pt-5 sm:flex-row sm:items-end sm:justify-between sm:px-8">
          <div>
            <p className="text-xs font-semibold uppercase text-[#f47b45]">/smoking-debug</p>
            <h1 className="mt-2 text-4xl font-black tracking-normal text-[#f7f1e6] sm:text-6xl">Breath Draw</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="h-3 w-3 rounded-full bg-[#66d7c4]" style={{ opacity: detector.isListening ? 1 : 0.28 }} />
            <span className="font-mono text-sm text-[#d8d1c4]">{modeLabel(detector)}</span>
          </div>
        </div>

        <div className="relative flex-1">
          <DebugSmokingCanvas levelsRef={detector.levelsRef} />
        </div>

        <div className="grid min-w-0 gap-4 px-5 py-5 sm:px-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
          <div className="grid min-w-0 gap-4 rounded-sm border border-[#3b3730] bg-[#181715] p-4 sm:grid-cols-3">
            <Meter label="inhale" value={detector.levels.inhale} colorClass="bg-[#ff6a34]" />
            <Meter label="exhale" value={detector.levels.exhale} colorClass="bg-[#97e7d9]" />
            <Meter label="signal" value={detector.levels.loudness} colorClass="bg-[#f5cf58]" />
            <div className="sm:col-span-3">
              <SensitivityControl value={detector.sensitivity} onChange={detector.setSensitivity} />
            </div>
            <textarea
              id="smoking-model-export"
              readOnly
              value={detector.exportJson}
              className="h-32 resize-none rounded-sm border border-[#3b3730] bg-[#0d0c0b] p-3 font-mono text-[10px] text-[#d8d1c4] outline-none sm:col-span-3"
              aria-label="Current breath model JSON"
            />
            <button
              type="button"
              onClick={detector.copyExport}
              className="rounded-sm border border-[#5d574f] px-4 py-3 text-sm font-bold text-[#d8d1c4] transition hover:bg-[#27231f] sm:col-span-3"
            >
              Copy model JSON
            </button>
            <div className="flex flex-wrap items-center justify-between gap-3 sm:col-span-3">
              <span className="text-xs font-semibold uppercase text-[#d8d1c4]">samples</span>
              <span className="font-mono text-xs text-[#aaa196]">
                {detector.sampleSessionCount} sessions / {detector.sampleFrameCount} frames
              </span>
            </div>
            <textarea
              id="smoking-samples-export"
              readOnly
              value={detector.samplesJson}
              className="h-32 resize-none rounded-sm border border-[#3b3730] bg-[#0d0c0b] p-3 font-mono text-[10px] text-[#d8d1c4] outline-none sm:col-span-3"
              aria-label="Current breath sample dataset JSON"
            />
            <div className="flex flex-wrap gap-2 sm:col-span-3">
              <button
                type="button"
                onClick={detector.copySamples}
                className="rounded-sm border border-[#5d574f] px-4 py-3 text-sm font-bold text-[#d8d1c4] transition hover:bg-[#27231f]"
              >
                Copy samples JSON
              </button>
              <button
                type="button"
                onClick={detector.resetSamples}
                className="rounded-sm border border-[#5d574f] px-4 py-3 text-sm font-bold text-[#d8d1c4] transition hover:bg-[#27231f]"
              >
                Reset samples
              </button>
            </div>
          </div>

          <div className="min-w-0 rounded-sm border border-[#3b3730] bg-[#181715] p-4">
            <div className="flex flex-wrap gap-2">
              <BreathButton detector={detector} compact />
              <button
                type="button"
                onClick={() => detector.startTraining("inhale")}
                disabled={!detector.isListening}
                className="rounded-sm border border-[#ff6a34] px-4 py-3 text-sm font-bold text-[#ffb099] transition hover:bg-[#391b11] disabled:cursor-not-allowed disabled:border-[#3b3730] disabled:text-[#6f685f]"
              >
                Record inhale
              </button>
              <button
                type="button"
                onClick={() => detector.startTraining("exhale")}
                disabled={!detector.isListening}
                className="rounded-sm border border-[#66d7c4] px-4 py-3 text-sm font-bold text-[#b9fff4] transition hover:bg-[#112c29] disabled:cursor-not-allowed disabled:border-[#3b3730] disabled:text-[#6f685f]"
              >
                Record exhale
              </button>
              <button
                type="button"
                onClick={detector.resetModel}
                className="rounded-sm border border-[#5d574f] px-4 py-3 text-sm font-bold text-[#d8d1c4] transition hover:bg-[#27231f]"
              >
                Reset
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
              <p className="min-w-0 break-words font-mono text-sm text-[#aaa196]" role="status">{detector.message}</p>
              <p className="shrink-0 font-mono text-xs text-[#847b71]">{detector.trainedFrames} frames</p>
            </div>

            {detector.training ? (
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#312d28]">
                <div
                  className="h-full rounded-full bg-[#f5efe4]"
                  style={{ width: `${Math.round(detector.training.progress * 100)}%` }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </main>
  );
}

function SmokingPage() {
  const detector = useBreathDetector();
  const didRequestMicRef = useRef(false);

  useEffect(() => {
    if (didRequestMicRef.current) {
      return;
    }

    didRequestMicRef.current = true;
    detector.startMic();
  }, [detector]);

  return <PublicSmokingScene detector={detector} />;
}

export { SmokingDebugPage };
export default SmokingPage;
